import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';

import 'tinymce';
import 'tinymce/themes/modern';
import 'tinymce/plugins/table';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/paste';

import { Subject } from 'rxjs/Subject';
import { Content, ModalController, Platform } from 'ionic-angular';
import { ChooseFilesModalComponent } from '@mob-components/files/modals/choose-files-modal/choose-files-modal';

import { variablesLists } from '@models/variables-to-insert';
import { FileUploadDataProvider } from '@services/files/file-upload.data.service';

import { FileUploadServiceProvider } from '@services/files/file-upload-service/file-upload.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { EmailServiceProvider } from '@services/email/email-service/email.service';
import { Keyboard } from '@ionic-native/keyboard';

declare const tinymce: any;

@Component({
	selector: 'sn-tiny-editor',
	template: require('./sn-tiny-editor.html')
})
export class SnTinyEditorComponent
	implements AfterViewInit, OnChanges, OnInit, OnDestroy {
	@Input() elementId: string;
	@Input() value: string;
	@Input() varsList: string;
	@Input() toolbar: string;
	@Input() filesObj: any;
	@Input() height: number;
	@Input() content: Content;
	@Input() isQBIntegrated?: boolean;
	@Output() onTextChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onAttachmentsChange: EventEmitter<any> = new EventEmitter<any>();

	editor;
	variables: any;
	isStoredFiles: boolean;
	isKeyboardVisible = false;
	private unsubscribe: Subject<void> = new Subject<void>();
	private cursorPosition;
	private lookedLinks = [
		'%contract_link%',
		'%invoice_link%',
		'%quote_link%',
		'%questionnaire_link%'
	];

	private toolbars = {
		basicToolbar:
			'bold italic underline | bullist numlist | link | alignleft aligncenter alignright alignjustify',
		basicWithAttachments:
			'bold italic underline | bullist numlist | link attachments | alignleft aligncenter alignright alignjustify ',
		toolbarWithVariables:
			'bold italic underline | bullist numlist | link attachments | alignleft aligncenter alignright alignjustify | variables'
	};

	constructor(
		private modalCtrl: ModalController,
		private filesDataService: FileUploadDataProvider,
		private filesService: FileUploadServiceProvider,
		private messageService: MessageService,
		private keyboard: Keyboard,
		private platform: Platform,
		private element: ElementRef,
		private emailService: EmailServiceProvider
	) {}

	private getVariablesInFormat(editor, variables) {
		const varsInFormat = [];
		for (const item in variables) {
			const text = item;
			const value = variables[item];
			const onclick = () => {
				editor.insertContent(value);
			};
			varsInFormat.push({ text, onclick });
		}

		return varsInFormat;
	}

	private addVariables(editor, inputVarsList) {
		const defaultVarsList = 'basicMenu';
		let variablesToInsert;
		if (inputVarsList in variablesLists) {
			variablesToInsert = variablesLists[inputVarsList];
		} else {
			variablesToInsert = variablesLists[defaultVarsList];
		}

		return this.getVariablesInFormat(editor, variablesToInsert);
	}

	private addFilesMenu = () => {
		const menu = [
			{
				text: 'From Files',
				disabled: !this.isStoredFiles,
				minWidth: 100,
				onclick: () => {
					this.showChooseFileModal();
				}
			},
			{
				text: 'From Device',
				minWidth: 100,
				onclick: () => {
					this.attachFileFromComputer();
				}
			}
		];
		return menu;
	};

	private showChooseFileModal() {
		const chooseFileModal = this.modalCtrl.create(ChooseFilesModalComponent);
		chooseFileModal.present();
	}

	private setEditorContent = () => {
		const isValueDefined = this.value !== undefined && this.value !== null;
		if (isValueDefined) {
			this.value = this.emailService.prepareSignatureToShow(this.value);
			this.saveSelection();
			this.editor.setContent(this.value);
			this.restoreSelection(this.cursorPosition);
		}
	};

	private checkLinkValidation = () => {
		const fn = this.editor.windowManager.open;

		// overwrites default windowManager function
		this.editor.windowManager.open = (t, r) => {
			if (t.title === 'Insert link') {
				const oldSubmit = t.onSubmit;

				t.onSubmit = e => {
					const isEmail = this.isMail(e.data.href);

					if (this.isIncorrectUrl(e.data.href)) {
						if ((isEmail && this.isIncorrectMail(e.data.href)) || !isEmail) {
							this.messageService.showToaster('e_0', 'generalValidation');
							return;
						}
					}

					if (isEmail) {
						e.data.href = `mailto:${e.data.href}`;
					}

					return oldSubmit(e);
				};

				// call the original submit function
				fn.apply(this, [t, r]);
			} else {
				// call default function for other modals
				return fn(t, r);
			}
		};
	};

	private attachFileFromComputer = (): void => {
		const input: HTMLElement = document.querySelector(
			'.attachments__input'
		) as HTMLElement;
		input.click();
	};

	private getToolbar = (toolbarType = 'basicToolbar') =>
		this.toolbars[toolbarType] || null;

	private getAttachments() {
		this.filesDataService.checkedFilesObservable
			.takeUntil(this.unsubscribe)
			.subscribe((chosenFiles: Array<any>) => {
				if (this.filesObj !== undefined && chosenFiles !== null) {
					const newAttachmentsSize = this.filesService.getAllAttachmentsSize(
						this.filesObj.filesSize,
						chosenFiles
					);
					const maxSize = this.filesService.maxAttachment;
					if (newAttachmentsSize > maxSize) {
						this.messageService.showToaster('w_0', 'attachments');
						return;
					}
					this.filesObj.filesSize = newAttachmentsSize;
					chosenFiles.forEach(chosenFile => {
						let showErrorMessage = false;
						if (this.checkIfFileExist(chosenFile) === -1) {
							this.filesObj.attachments.push(chosenFile);
						} else {
							showErrorMessage = true;
						}
						if (showErrorMessage) {
							this.messageService.showToaster('e_1', 'attachments');
						}
					});
				}
			});
	}

	private checkIfFileExist(chosenFile) {
		return this.filesObj.attachments.findIndex(
			file => file.id === chosenFile.id
		);
	}

	private checkJobFiles() {
		this.filesDataService.filesObservable
			.takeUntil(this.unsubscribe)
			.subscribe((storedFiles: Array<any>) => {
				this.isStoredFiles = !!storedFiles.length;
			});
	}

	private isIncorrectUrl(url: string): boolean {
		if (url.startsWith('http://') && url.length > 7) {
			return false;
		}

		if (url.startsWith('https://') && url.length > 8) {
			return false;
		}

		for (let i = 0, max = this.lookedLinks.length; i < max; i++) {
			if (url.includes(this.lookedLinks[i])) {
				return false;
			}
		}

		return true;
	}

	private isMail = (email: string) => email.includes('@');

	private isIncorrectMail(email) {
		const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return !emailRegex.test(email);
	}

	private saveSelection() {
		this.cursorPosition = this.editor.selection.getBookmark(2, true);
	}

	private restoreSelection(bookmark) {
		this.editor.selection.moveToBookmark(bookmark);
	}

	emitNewAttachments($event) {
		this.onAttachmentsChange.emit($event);
	}

	private moveCursorToEnd() {
		this.editor.focus();
		this.editor.selection.select(this.editor.getBody(), true);
		this.editor.selection.collapse(false);
	}

	ngOnInit() {
		this.getAttachments();
		this.checkJobFiles();
	}

	ngOnChanges() {
		if (this.editor !== undefined) {
			this.setEditorContent();
		}
	}

	ngOnDestroy() {
		tinymce.remove(this.editor);
		this.filesDataService.setCheckedFiles(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngAfterViewInit() {
		tinymce.init({
			anchor_bottom: false,
			anchor_top: false,
			branding: false,
			content_css: 'assets/skins/tiny-editor-content.css',
			height: this.height || 200,
			init_instance_callback: this.setEditorContent,
			selector: `#${this.elementId}`,
			plugins: ['link', 'lists', 'paste'],
			paste_as_text: false,
			paste_webkit_styles: 'all',
			paste_filter_drop: false,
			paste_enable_default_filters: false,
			paste_remove_styles_if_webkit: false,
			paste_retain_style_properties: 'all',
			target_list: false,
			link_title: false,
			menubar: false,
			max_chars: 4000,
			resize: false,
			statusbar: false,
			skin_url: 'assets/skins/lightgray',
			toolbar: this.getToolbar(this.toolbar),
			setup: editor => {
				this.editor = editor;

				editor.on('init', () => {
					this.checkLinkValidation();
					editor.selection.select(editor.getBody(), true);
					editor.selection.collapse(false);
				});

				editor.on('BeforeSetContent', () => {
					this.restoreSelection(this.cursorPosition);
				});

				editor.on('ExecCommand', () => {
					this.saveSelection();
				});

				editor.on('Focus', () => {
					const offset = this.element.nativeElement.offsetTop;
					this.content.scrollTo(0, offset);
				});

				if (editor.id === 'add-product') {
					editor.on('KeyDown PastePostProcess', e => {
						const allowedKeys: Array<Number> = [8, 37, 38, 39, 40, 46];
						const rawContent: string = editor.getContent({ format: 'text' });
						switch (e.type) {
							case 'keydown':
								if (
									allowedKeys.indexOf(e.keyCode) === -1 &&
									rawContent.length > editor.settings.max_chars &&
									this.isQBIntegrated
								) {
									this.messageService.showToaster('w_0', 'qb');
									e.preventDefault();
									e.stopPropagation();
								}
								break;
							case 'pastepostprocess':
								if (
									(rawContent + e.node.innerText).length >
										editor.settings.max_chars &&
									this.isQBIntegrated
								) {
									this.messageService.showToaster('w_0', 'qb');
								}
								break;
							default:
								break;
						}
					});
				}

				editor.on('keyup change', () => {
					const content = editor.getContent();
					this.onTextChange.emit(content);
				});

				editor.addButton('variables', {
					type: 'splitbutton',
					text: 'Insert variable',
					icon: false,
					menu: [...this.addVariables(editor, this.varsList)]
				});

				editor.addButton('attachments', {
					icon: 'fa fa-paperclip',
					menu: [...this.addFilesMenu()],
					type: 'splitbutton'
				});
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./mobile/src/components/shared/sn-tiny-editor/sn-tiny-editor.ts